import React, {useEffect, useMemo, useState} from 'react';
import {
  Platform,
  StyleSheet,
  ToastAndroid,
  View,
} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {messageOf} from '@/shared/lib/errors';
import type {NativeStackScreenProps} from '@react-navigation/native-stack';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import type {DevicesStackParamList} from '@/app/routes';
import {approveEnrollment, fetchEnrollment} from '@/entities/enrollment/api';
import type {EnrollmentStatus} from '@/entities/enrollment/types';
import {adoptByCode, claimMachine, fetchMachine, releaseMachine} from '@/entities/machine/api';
import {LicenseNotice} from '@/features/view-license/ui/LicenseNotice';
import type {MachineCard} from '@/entities/machine/types';
import {DepartmentPicker} from '@/features/manage-personnel/ui/DepartmentPicker';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {Field, FieldGroup} from '@/shared/ui/field';
import {Icon} from '@/shared/ui/icons';
import {Skeleton} from '@/shared/ui/skeleton';
import {AppInput} from '@/shared/ui/input';
import {Page} from '@/shared/ui/page';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Step two of adoption: the card behind whatever was scanned, then the one write
 * the Home context can make to it — connect it to the personal workspace.
 *
 * Owner self-service: both paths send the active company as X-Company-Id, which
 * the gateway checks against the caller's `owner` role in that company's domain
 * and pins the connected company to. The company also rides in the body,
 * matching the header.
 *
 * **Two paths, because two generations of firmware are in the field.** A current
 * terminal is a *machine*: it licensed itself on first boot, its number never
 * expires, and connecting it grants no licence because it already has one. An
 * older terminal raises an *enrollment* — a pairing code that dies in minutes,
 * and whose approval is what mints the licence. The machine path is where this
 * is going; the enrollment path stays until the last old terminal is updated.
 *
 * What blocks a connection differs accordingly, and that is an honest difference
 * rather than an inconsistency: an enrollment can time out, a machine cannot.
 *
 * **One of the blockers has an undo, and this is the only screen that can offer it.**
 * "Already connected — to you" used to be a dead end: the machine was recorded against
 * this company, and nothing in the cabinet could take it off again, so a board that had
 * been decommissioned or moved to another site stayed claimed forever and answered
 * "already claimed" wherever it was scanned next. `POST /admin/machines/{number}/release`
 * is the undo, it is owner self-service, and it needs the machine NUMBER — which no
 * other screen has, because a device card carries a device id and not this. It touches
 * no licence and wipes no board; it only stops the machine belonging here.
 */
type Props = NativeStackScreenProps<DevicesStackParamList, 'ConfirmAdopt'>;

/** Enrollment lifecycle → status tone. Ready is teal, timed-out is amber, the rest grey. */
function statusTone(status: EnrollmentStatus): StatusTone {
  switch (status) {
    case 'PENDING':
    case 'APPROVED':
      return 'online';
    case 'EXPIRED':
      return 'pending';
    default:
      return 'neutral';
  }
}

/**
 * Why this machine cannot be connected, or null when it can.
 *
 * REVOKED is a terminal somebody reported stolen. A machine already held by
 * another company is not something we can resolve from here — they release it,
 * or it was never ours. A machine held by *us* is simply already connected.
 */
function machineBlocker(
  card: MachineCard,
  companyId: string | undefined,
): string | null {
  if (card.status === 'REVOKED') {
    return 'home.adopt.confirm.machineRevoked';
  }
  if (isOurs(card, companyId)) {
    return 'home.adopt.confirm.claimed';
  }
  if (card.company_id) {
    return 'home.adopt.confirm.machineTaken';
  }
  return null;
}

/**
 * Held by THIS company — the one blocker that is ours to undo.
 *
 * Compared case-insensitively: `company_id` comes back from sv_license and the active
 * context's id came from sv_organization, and a uuid that differs only in case would
 * silently read as somebody else's terminal and hide the release button.
 */
function isOurs(card: MachineCard, companyId: string | undefined): boolean {
  if (!card.company_id || !companyId) {
    return false;
  }
  return card.company_id.trim().toLowerCase() === companyId.trim().toLowerCase();
}

const styles = StyleSheet.create({
  /* Page не раздвигает детей сама, а карточка машины, срок лицензии, поле имени,
     отдел и кнопка — пять самостоятельных блоков: без общего gap «Подключить»
     прилипала к чипам отдела. Тот же приём, что `manual` в ScanDeviceScreen. */
  body: {gap: spacing(4)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  headText: {flex: 1, gap: spacing(2)},
  section: {gap: spacing(2)},
  loading: {gap: spacing(4)},
});

export function ConfirmAdoptScreen({route, navigation}: Props) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const target = route.params;
  const companyId = useAuthStore(s => s.activeContext?.companyId);

  // «Сегодня» читается один раз: срок лицензии не должен сдвинуться на день посреди
  // экрана, если его оставили открытым до полуночи.
  const now = useMemo(() => Date.now(), []);

  const isMachine = target.kind === 'machine';
  const machineNumber = target.kind === 'machine' ? target.machineNumber : '';
  const pairingCode = target.kind === 'enrollment' ? target.pairingCode : '';

  const [deviceName, setDeviceName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [confirmRelease, setConfirmRelease] = useState(false);

  const machineQuery = useQuery({
    queryKey: ['machine', machineNumber, companyId],
    queryFn: () => fetchMachine(machineNumber, companyId!),
    enabled: !!companyId && isMachine,
  });

  // Заявка может не найтись — и это не обязательно ошибка. Восьмизначная
  // строка сегодня бывает двух видов: заявка старой прошивки и одноразовый код
  // подключения к уже существующей машине. Отличить их по виду нельзя, поэтому
  // сначала спрашиваем про заявку, а её отсутствие читаем как «значит, код».
  const enrollQuery = useQuery({
    queryKey: ['enrollment', pairingCode, companyId],
    queryFn: () => fetchEnrollment(pairingCode, companyId!),
    enabled: !!companyId && !isMachine,
    retry: false,
  });

  /** Заявки нет, но строка похожа на код подключения — путь через машину. */
  const looksLikeAdoptionCode =
    !isMachine && !enrollQuery.isPending && !enrollQuery.data;

  const query = isMachine ? machineQuery : enrollQuery;

  // What to default the name from. A machine reports its own once it has one;
  // before that, and for an enrollment, the model.
  const suggestedName = isMachine
    ? machineQuery.data?.name
    : enrollQuery.data?.model;
  const hardwareId = isMachine
    ? machineQuery.data?.hardware_id
    : enrollQuery.data?.hardware_id;

  // Default the name, but only until the user edits it themselves.
  useEffect(() => {
    if (suggestedName && !nameTouched) {
      setDeviceName(suggestedName);
    }
  }, [suggestedName, nameTouched]);

  /**
   * Почему не подключилось — словами, а не «попробуйте ещё раз» (SV-48).
   *
   * Сервер объясняет отказ сам; известные ответы переводим, остальные
   * показываем дословно. Прежде всё сводилось к общей фразе, и человек
   * повторял то, что не сработает: «уже машина X» не лечится повтором —
   * её подключают по номеру.
   */
  const adoptErrorText = (error: unknown): string => {
    const raw = error instanceof Error ? error.message : '';
    const machine = /already machine (\S+)/.exec(raw);
    if (machine) {
      return t('home.adopt.confirm.alreadyMachine', {number: machine[1]});
    }
    if (/already connected to another company/.test(raw)) {
      return t('home.adopt.confirm.machineTaken');
    }
    if (/waiting for an administrator to activate/.test(raw)) {
      return t('home.adopt.confirm.machinePending');
    }
    if (/has been retired/.test(raw)) {
      return t('home.adopt.confirm.machineRevoked');
    }
    return messageOf(error, t('home.adopt.confirm.failed'));
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!companyId) {
        throw new Error('missing active company');
      }
      if (target.kind === 'machine') {
        return claimMachine(target.machineNumber, {
          companyId,
          deviceName: deviceName.trim(),
          departmentId: departmentIds[0],
        });
      }
      if (looksLikeAdoptionCode) {
        return adoptByCode(target.pairingCode, {
          companyId,
          deviceName: deviceName.trim(),
        });
      }
      return approveEnrollment(target.pairingCode, {
        companyId,
        deviceName: deviceName.trim(),
        departmentId: departmentIds[0],
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['devices']});
      if (Platform.OS === 'android') {
        ToastAndroid.show(t('home.adopt.confirm.success'), ToastAndroid.SHORT);
      }
      // Drop both scan and confirm off the stack, landing back on the (now stale,
      // about-to-refetch) device list.
      navigation.popToTop();
    },
  });

  /**
   * Take the machine back off this company.
   *
   * Stays on the screen rather than popping: the card re-reads and the blocker turns
   * into the connect form, which is exactly the sequence somebody moving a board to a
   * different department or workspace wants — release, then reconnect, in one place.
   */
  const release = useMutation({
    mutationFn: () => releaseMachine(machineNumber, companyId!),
    onSuccess: async () => {
      setConfirmRelease(false);
      await Promise.all([
        machineQuery.refetch(),
        qc.invalidateQueries({queryKey: ['devices']}),
      ]);
    },
  });

  const machine = machineQuery.data;
  const enrollment = enrollQuery.data;

  let body: React.ReactNode;
  if (query.isPending) {
    body = (
      <View style={styles.loading}>
        <Skeleton height={64} radius={radii.lg} />
        <Skeleton height={140} radius={radii.lg} />
      </View>
    );
  } else if (looksLikeAdoptionCode) {
    // Показывать нечего — карточки у кода нет, — но и отказывать нельзя:
    // сервер узнает машину по коду сам. Спрашиваем имя и подключаем.
    body = (
      <Card padding={4} style={{gap: spacing(3)}}>
        <AppText variant="label">{t('home.adopt.confirm.codeOnly')}</AppText>
        <AppText variant="caption" color={p.mutedForeground}>
          {t('home.adopt.confirm.codeOnlyHint')}
        </AppText>
        <AppInput
          value={deviceName}
          onChangeText={(v: string) => {
            setNameTouched(true);
            setDeviceName(v);
          }}
          placeholder={t('home.adopt.confirm.namePlaceholder')}
        />
        <AppButton
          title={t('home.adopt.confirm.connect')}
          loading={mutation.isPending}
          disabled={mutation.isPending}
          onPress={() => mutation.mutate()}
        />
      </Card>
    );
  } else if (query.isError || (isMachine ? !machine : !enrollment)) {
    body = (
      <EmptyState
        tone="error"
        title={t(
          isMachine
            ? 'home.adopt.confirm.machineNotFound'
            : 'home.adopt.confirm.notFound',
        )}
        actionLabel={t('actions.retry')}
        onAction={() => {
          void query.refetch();
        }}
      />
    );
  } else {
    const blockedKey = machine
      ? machineBlocker(machine, companyId)
      : enrollment!.status === 'EXPIRED'
      ? 'home.adopt.confirm.expired'
      : enrollment!.status === 'CLAIMED'
      ? 'home.adopt.confirm.claimed'
      : null;

    body = (
      <View style={styles.body}>
        {/* The terminal you are about to take responsibility for, stated the way the
            device list will state it afterwards — same glyph, same status pill. */}
        <View style={styles.head}>
          <IconBubble tone={p.accent} size={52} square>
            <Icon name="devices" size={26} color={p.primary} />
          </IconBubble>
          <View style={styles.headText}>
            <AppText variant="title">{t('home.adopt.confirm.title')}</AppText>
            <StatusPill
              tone={
                machine
                  ? machine.status === 'ACTIVE'
                    ? 'online'
                    : 'neutral'
                  : statusTone(enrollment!.status)
              }
              size="sm"
              label={
                machine
                  ? t(`home.adopt.machineStatus.${machine.status}`)
                  : t(`home.adopt.status.${enrollment!.status}`)
              }
            />
          </View>
        </View>

        <FieldGroup>
          {machine ? (
            <Field
              label={t('home.adopt.confirm.machineNumber')}
              value={machine.machine_number}
              mono
            />
          ) : (
            <Field
              label={t('home.adopt.confirm.model')}
              value={enrollment!.model}
            />
          )}
          <Field
            label={t('home.adopt.confirm.hardwareId')}
            value={hardwareId}
            mono
          />
        </FieldGroup>

        {/* Лицензия видна кабинету только здесь — в карточке машины, пока её номер в
            руках (разбор: entities/machine/license.ts). Подключение лицензию не
            выдаёт и не продлевает, поэтому дата, которую видно сейчас, останется той
            же и после «Подключить»: это последний экран, где о ней вообще сообщат. */}
        {machine ? (
          <LicenseNotice license={machine.license} now={now} />
        ) : null}

        {blockedKey ? (
          <>
            <Banner
              tone={blockedKey === 'home.adopt.confirm.expired' ? 'warning' : 'info'}
              title={t(blockedKey)}
            />
            {/* The only blocker with an undo: it is our own company holding it. */}
            {machine && isOurs(machine, companyId) ? (
              <View style={styles.section}>
                <AppText variant="caption">
                  {t('home.adopt.confirm.releaseHint')}
                </AppText>
                {release.isError ? (
                  <Banner tone="error" title={t('home.adopt.confirm.releaseFailed')} />
                ) : null}
                {confirmRelease ? (
                  <>
                    <Banner
                      tone="warning"
                      title={t('home.adopt.confirm.releaseConfirm')}
                      message={t('home.adopt.confirm.releaseScope')}
                    />
                    <AppButton
                      title={t('home.adopt.confirm.releaseDo')}
                      tone="destructive"
                      size="sm"
                      loading={release.isPending}
                      onPress={() => release.mutate()}
                      testID="machine-release-confirm"
                    />
                    <AppButton
                      title={t('actions.cancel')}
                      tone="ghost"
                      size="sm"
                      onPress={() => setConfirmRelease(false)}
                    />
                  </>
                ) : (
                  <AppButton
                    title={t('home.adopt.confirm.release')}
                    tone="neutral"
                    size="sm"
                    onPress={() => setConfirmRelease(true)}
                    testID="machine-release"
                  />
                )}
              </View>
            ) : null}
          </>
        ) : (
          <>
            <AppInput
              label={t('home.adopt.confirm.deviceName')}
              value={deviceName}
              onChangeText={value => {
                setNameTouched(true);
                setDeviceName(value);
              }}
              placeholder={t('home.adopt.confirm.deviceNamePlaceholder')}
            />
            {companyId ? (
              <View style={styles.section}>
                <AppText variant="label">
                  {t('home.adopt.confirm.departmentOptional')}
                </AppText>
                <DepartmentPicker
                  companyId={companyId}
                  value={departmentIds}
                  onChange={ids => setDepartmentIds(ids.slice(-1))}
                />
              </View>
            ) : null}
            {mutation.isError ? (
              <Banner tone="error" title={adoptErrorText(mutation.error)} />
            ) : null}
            <AppButton
              title={t('home.adopt.confirm.connect')}
              tone="signal"
              size="lg"
              icon="check"
              loading={mutation.isPending}
              disabled={!companyId || deviceName.trim().length === 0}
              onPress={() => mutation.mutate()}
            />
          </>
        )}
      </View>
    );
  }

  return (
    <Page
      scroll
      width="form"
      title={t('home.adopt.confirm.title')}
      testID="adopt-confirm">
      {body}
    </Page>
  );
}
